import Papa, { ParseResult } from 'papaparse';

export interface SegmentedParseResult {
  segments: Map<string, number[]>;
  skippedCount: number;
  errors: string[];
}

/**
 * Parses a file with exactly 2 columns (NPI and Segment) and groups NPIs by segment value.
 * @param targetListFile - The CSV/TSV file to parse
 * @returns SegmentedParseResult with segments map, skipped count, and any errors
 */
export const parseSegmentedFile = async (targetListFile: File): Promise<SegmentedParseResult> => {
  if (!targetListFile) {
    return {
      segments: new Map(),
      skippedCount: 0,
      errors: ['No file provided'],
    };
  }

  try {
    const parsedResults: ParseResult<any> = await parseFile(targetListFile);
    return parseSegmentedData(parsedResults);
  } catch (error) {
    return {
      segments: new Map(),
      skippedCount: 0,
      errors: [error instanceof Error ? error.message : 'Failed to parse file'],
    };
  }
};

/**
 * Parses the file using PapaParse
 */
const parseFile = async (targetListFile: File): Promise<ParseResult<any>> => {
  return new Promise<ParseResult<any>>((resolve, reject) => {
    Papa.parse(targetListFile, {
      complete: (results: ParseResult<any>) => {
        resolve(results);
      },
      error: (error: Error) => {
        reject(error);
      },
      newline: '\n',
    });
  });
};

/**
 * Parses the segmented data from Papa parse results
 */
const parseSegmentedData = (results: ParseResult<any>): SegmentedParseResult => {
  const errors: string[] = [];
  const data = results?.data || [];

  if (data.length === 0) {
    return {
      segments: new Map(),
      skippedCount: 0,
      errors: ['File is empty'],
    };
  }

  // Get headers (first row)
  const headers: string[] = data[0] || [];

  // Validate exactly 2 columns
  if (headers.length !== 2) {
    return {
      segments: new Map(),
      skippedCount: 0,
      errors: [
        `Segmented upload requires exactly 2 columns (NPI and Segment). Found ${headers.length} columns.`,
      ],
    };
  }

  // Find NPI and Segment column indices
  const npiIndex = findNpiColumnIndex(headers);
  const segmentIndex = findSegmentColumnIndex(headers);

  if (npiIndex === -1) {
    return {
      segments: new Map(),
      skippedCount: 0,
      errors: ['Could not find column with header "NPI" (case insensitive)'],
    };
  }

  if (segmentIndex === -1) {
    return {
      segments: new Map(),
      skippedCount: 0,
      errors: ['Could not find column with header "Segment" (case insensitive)'],
    };
  }

  // Group NPIs by segment
  const segments = new Map<string, Set<number>>();
  let skippedCount = 0;

  // Skip header row
  for (let i = 1; i < data.length; i++) {
    const row = data[i];
    if (!row || row.length === 0) continue;

    const npiValue = row[npiIndex]?.toString().trim();
    const segmentValue = row[segmentIndex]?.toString().trim();

    // Skip rows with empty segment values
    if (!segmentValue) {
      skippedCount++;
      continue;
    }

    // Validate NPI
    if (!isValidNpi(npiValue)) {
      continue; // Skip invalid NPIs silently (same as existing behavior)
    }

    const npi = parseInt(npiValue, 10);

    // Add to segments map
    if (!segments.has(segmentValue)) {
      segments.set(segmentValue, new Set());
    }
    segments.get(segmentValue)!.add(npi);
  }

  // Convert Sets to Arrays for easier consumption
  const segmentMap = new Map<string, number[]>();
  segments.forEach((npiSet, segment) => {
    segmentMap.set(segment, Array.from(npiSet));
  });

  // Check if any segments were found
  if (segmentMap.size === 0) {
    errors.push('No valid segments with NPIs found in file');
  }

  return {
    segments: segmentMap,
    skippedCount,
    errors,
  };
};

/**
 * Finds the index of the NPI column (case insensitive)
 */
const findNpiColumnIndex = (headers: string[]): number => {
  return headers.findIndex(header => header?.trim().toLowerCase() === 'npi');
};

/**
 * Finds the index of the Segment column (case insensitive)
 */
const findSegmentColumnIndex = (headers: string[]): number => {
  return headers.findIndex(header => header?.trim().toLowerCase() === 'segment');
};

/**
 * Validates if a value is a valid NPI number
 * - Exactly 10 digits
 * - Numeric
 * - Between 1,000,000,000 and 2,147,483,647 (32-bit int max for GraphQL)
 */
const isValidNpi = (value: string | undefined): boolean => {
  if (!value) return false;

  const trimmed = value.trim();

  // Check if it's exactly 10 digits
  if (trimmed.length !== 10) return false;

  // Check if it's numeric
  if (isNaN(parseInt(trimmed, 10))) return false;

  const npi = parseInt(trimmed, 10);

  // Check range: must be between 1B and 2.1B (32-bit int max)
  if (npi < 1000000000 || npi > 2147483647) return false;

  return true;
};
